import { useState } from 'react';
import useAsyncRetry from 'react-use/esm/useAsyncRetry';
import {
  InfoCard,
  Link,
  Progress,
  ResponseErrorPanel,
  StatusAborted,
  StatusError,
  StatusOK,
  StatusWarning,
  Table,
  TableColumn,
} from '@backstage/core-components';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import {
  ExternalSecretTarget,
  SecretAction,
  SecretState,
  Stage,
  VaultSecretRow,
  WriteResponse,
} from '@internal/backstage-plugin-keyvault-secrets-common';
import { useKeyVaultSecretsClient } from '../api';
import { AddDialog, ConfirmDialog, EditDialog, RemoveDialog } from './dialogs';

const ACTION_LABELS: Record<SecretAction, string> = {
  edit: 'Edit',
  'open-mapping-pr': 'Add to app',
  remove: 'Remove',
  disable: 'Disable',
  enable: 'Enable',
};

function StateLabel({ state }: { state: SecretState }) {
  switch (state) {
    case 'mapped':
      return <StatusOK>In use</StatusOK>;
    case 'mapped-disabled':
      return <StatusError>Disabled, the app gets no updates</StatusError>;
    case 'mapped-missing':
      return <StatusError>Missing in the vault</StatusError>;
    case 'orphan':
      return <StatusWarning>Not in use</StatusWarning>;
    default:
      return <StatusAborted>Disabled</StatusAborted>;
  }
}

function formatTime(iso: string | undefined): string {
  return iso ? new Date(iso).toLocaleString() : '-';
}

/** Each env variable that reads the secret, with the components that read it. */
function envVariables(row: VaultSecretRow): string[] {
  const components = new Map<string, Set<string>>();
  for (const { name, component } of row.mappings.flatMap(mapping => mapping.env)) {
    components.set(name, (components.get(name) ?? new Set<string>()).add(component));
  }
  return [...components].map(([name, set]) => `${name} (${[...set].sort().join(', ')})`);
}

type Open =
  | { kind: 'add' }
  | { kind: SecretAction; row: VaultSecretRow };

export function StageSecretsCard(props: {
  entityRef: string;
  stage: Stage;
  canWrite: boolean;
}) {
  const { entityRef, stage, canWrite } = props;
  const client = useKeyVaultSecretsClient();
  const { value, loading, error, retry } = useAsyncRetry(
    () => client.list(entityRef, stage),
    [client, entityRef, stage],
  );
  const [open, setOpen] = useState<Open | null>(null);
  const [result, setResult] = useState<WriteResponse>();

  const title = `Secrets in ${stage}`;
  if (loading && !value) {
    return (
      <InfoCard title={title}>
        <Progress />
      </InfoCard>
    );
  }
  if (error || !value) {
    return (
      <InfoCard title={title}>
        <ResponseErrorPanel error={error ?? new Error('No response')} />
      </InfoCard>
    );
  }

  const externalSecrets = value.externalSecrets;
  const canAdd = externalSecrets.some(target => target.component);

  // The list reloads after a refused write too: a refusal often means the
  // state changed since it was loaded.
  const write = async (call: () => Promise<WriteResponse>) => {
    try {
      const response = await call();
      setOpen(null);
      setResult(response);
    } finally {
      retry();
    }
  };

  const columns: TableColumn<VaultSecretRow>[] = [
    {
      title: 'Secret',
      field: 'vaultName',
      render: row => <code>{row.vaultName}</code>,
    },
    { title: 'Status', render: row => <StateLabel state={row.state} /> },
    {
      title: 'Env variables',
      render: row => {
        const names = envVariables(row);
        if (names.length > 0) {
          return names.map(name => (
            <Typography key={name} variant="body2">
              <code>{name}</code>
            </Typography>
          ));
        }
        return row.mappings.length > 0 ? 'Not read by the app' : '-';
      },
    },
    { title: 'Updated', render: row => formatTime(row.updatedOn) },
    {
      title: 'Pull requests',
      render: row =>
        row.pullRequests.length === 0 ? (
          '-'
        ) : (
          <>
            {row.pullRequests.map(pr => (
              <Typography key={pr.url} variant="body2">
                <Link to={pr.url}>
                  {pr.kind === 'add' ? 'Add' : 'Remove'} PR #{pr.number}
                </Link>
              </Typography>
            ))}
          </>
        ),
    },
  ];
  if (canWrite) {
    columns.push({
      title: 'Actions',
      render: row => (
        <Box display="flex" gridGap={4} flexWrap="wrap">
          {row.actions.map(action => (
            <Button
              key={action}
              size="small"
              variant="outlined"
              color={action === 'remove' || action === 'disable' ? 'secondary' : 'primary'}
              aria-label={`${ACTION_LABELS[action]} ${row.vaultName}`}
              onClick={() => setOpen({ kind: action, row })}
            >
              {ACTION_LABELS[action]}
            </Button>
          ))}
        </Box>
      ),
    });
  }

  return (
    <InfoCard
      title={title}
      subheader={`Vault ${value.vault}`}
      action={
        canWrite && canAdd ? (
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpen({ kind: 'add' })}
          >
            Add secret
          </Button>
        ) : undefined
      }
    >
      {result && (
        <Box pb={2}>
          <Alert
            severity={result.failedStep ? 'warning' : 'success'}
            onClose={() => setResult(undefined)}
          >
            {result.failedStep ? `Partly done: ${result.message}` : result.message}
            {result.pullRequest && (
              <>
                {' '}
                <Link to={result.pullRequest.url}>
                  {result.pullRequest.kind} PR #{result.pullRequest.number}
                </Link>
              </>
            )}
          </Alert>
        </Box>
      )}
      {value.warnings.map(warning => (
        <Box pb={2} key={warning}>
          <Alert severity="warning">{warning}</Alert>
        </Box>
      ))}

      <Table
        options={{ search: false, paging: false, toolbar: false, padding: 'dense' }}
        columns={columns}
        data={value.secrets}
      />

      <Box pt={2}>
        <Typography variant="body2" color="textSecondary">
          The pods get a change within 1 hour, then restart.
        </Typography>
        {externalSecrets.map((target: ExternalSecretTarget) => (
          <Typography key={target.externalSecretId} variant="body2" color="textSecondary">
            Last sync of {target.component ?? `Secret ${target.secretName}`}:{' '}
            {formatTime(target.lastRefresh)}
            {target.ready === false ? ' (failing)' : ''}
          </Typography>
        ))}
      </Box>

      {open?.kind === 'add' && (
        <AddDialog
          externalSecrets={externalSecrets}
          onSubmit={request => write(() => client.add(entityRef, stage, request))}
          onClose={() => setOpen(null)}
        />
      )}
      {open?.kind === 'open-mapping-pr' && (
        <AddDialog
          externalSecrets={externalSecrets}
          orphanVaultName={open.row.vaultName}
          onSubmit={request => write(() => client.add(entityRef, stage, request))}
          onClose={() => setOpen(null)}
        />
      )}
      {open?.kind === 'edit' && (
        <EditDialog
          row={open.row}
          onSubmit={secretValue =>
            write(() => client.edit(entityRef, stage, open.row.vaultName, { value: secretValue }))
          }
          onClose={() => setOpen(null)}
        />
      )}
      {open?.kind === 'remove' && (
        <RemoveDialog
          row={open.row}
          externalSecrets={externalSecrets}
          onSubmit={mappingIds =>
            write(() => client.remove(entityRef, stage, open.row.vaultName, { mappingIds }))
          }
          onClose={() => setOpen(null)}
        />
      )}
      {open?.kind === 'disable' && (
        <ConfirmDialog
          title={`Disable ${open.row.vaultName}`}
          text="Disables every version of this secret. The portal refuses while the app or an open add PR still uses it."
          submitLabel="Disable"
          onSubmit={() => write(() => client.disable(entityRef, stage, open.row.vaultName))}
          onClose={() => setOpen(null)}
        />
      )}
      {open?.kind === 'enable' && (
        <ConfirmDialog
          title={`Enable ${open.row.vaultName}`}
          text="Enables the latest version of this secret, so the app can read it again."
          submitLabel="Enable"
          onSubmit={() => write(() => client.enable(entityRef, stage, open.row.vaultName))}
          onClose={() => setOpen(null)}
        />
      )}
    </InfoCard>
  );
}
